// src/components/settings/Settings.jsx
import React, { useState, useEffect } from 'react';
import { 
  User, 
  Building, 
  Briefcase, 
  Bell, 
  Shield, 
  Save,
  ArrowLeft,
  Settings as SettingsIcon
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ProfileSettings from './ProfileSettings';
import InstitutionSettings from './InstitutionSettings';
import ProjectManager from '../projects/ProjectManager';
import NotificationSettings from './NotificationSettings';
import logo from '../../assets/images/logo.png';

const Settings = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const tabs = [
    {
      id: 'profile',
      label: 'Profile',
      icon: User,
      description: 'Personal information and account details'
    },
    {
      id: 'institution',
      label: 'Institution',
      icon: Building,
      description: 'Manage your educational institutions'
    },
    {
      id: 'projects',
      label: 'Projects',
      icon: Briefcase,
      description: 'Manage your educational projects'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      description: 'Email and push notification preferences'
    },
    {
      id: 'privacy',
      label: 'Privacy & Security',
      icon: Shield,
      description: 'Data sharing and security settings'
    }
  ];

  const handleTabChange = (tabId) => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave this section?')) {
        setHasUnsavedChanges(false);
        setActiveTab(tabId);
      }
    } else {
      setActiveTab(tabId);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileSettings onUnsavedChanges={setHasUnsavedChanges} />;
      case 'institution':
        return <InstitutionSettings onUnsavedChanges={setHasUnsavedChanges} />;
      case 'projects':
        return <ProjectManager onUnsavedChanges={setHasUnsavedChanges} />;
      case 'notifications':
        return <NotificationSettings onUnsavedChanges={setHasUnsavedChanges} />;
      case 'privacy':
        return <div className="p-6 text-center text-gray-500">Privacy & Security settings coming soon...</div>;
      default:
        return <ProfileSettings onUnsavedChanges={setHasUnsavedChanges} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Dashboard</span>
              </button>
              <div className="h-6 border-l border-gray-300"></div>
              <div className="flex items-center gap-3">
                <SettingsIcon className="w-5 h-5 text-gray-600" />
                <h1 className="text-xl font-semibold text-gray-900">Settings</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <img src={logo} alt="Taskdata Logo" className="h-8 w-auto" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-x-8">
          {/* Sidebar */}
          <aside className="py-6 px-2 sm:px-6 lg:py-0 lg:px-0 lg:col-span-3">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`group rounded-md px-3 py-2 flex items-center text-sm font-medium w-full text-left transition-colors ${
                      isActive
                        ? 'bg-blue-50 border-blue-500 text-blue-700 border-l-4'
                        : 'text-gray-900 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon
                      className={`flex-shrink-0 mr-3 h-5 w-5 ${
                        isActive
                          ? 'text-blue-500'
                          : 'text-gray-400 group-hover:text-gray-500'
                      }`}
                    />
                    <div className="flex-1">
                      <div className="font-medium">{tab.label}</div>
                      <div className="text-xs text-gray-500 hidden lg:block">
                        {tab.description}
                      </div>
                    </div>
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Main content */}
          <div className="space-y-6 sm:px-6 lg:px-0 lg:col-span-9">
            <div className="bg-white shadow-sm rounded-lg">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Unsaved changes warning */}
      {hasUnsavedChanges && (
        <div className="fixed bottom-4 right-4 bg-orange-100 border border-orange-200 rounded-lg p-4 shadow-lg">
          <div className="flex items-center gap-2">
            <Save className="w-4 h-4 text-orange-600" />
            <span className="text-sm text-orange-800 font-medium">
              You have unsaved changes
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;